import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {Registration} from '../../../shared/interface/registration';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {RegistrationService} from '../../../shared/services/registration.service';
import {Router} from '@angular/router';
import {first, takeUntil} from 'rxjs/operators';
import {
  IC_ALLIANZ,
  IC_COMPENSA, IC_COMPENSA_LIFE, IC_EUROP_ASSISTANCE,
  IC_EUROPA, IC_INTER_POLSKA, IC_INTER_POLSKA_LIFE,
  IC_LINK4,
  IC_PZU, IC_PZU_LIFE, IC_TRASTI,
  IC_WIENER, IC_WITH_SALES_PROFILES
} from '../../../shared/services/insurance-company.service';
import {
  DICT_TYPE_REGISTRATION_ID_TYPE,
  DICT_TYPE_TEMPLATE_TYPE,
  DictService,
  INSURANCE_TYPE_ASSETS,
  INSURANCE_TYPE_LIFE,
  MEETING_EXAM_TYPE_100P,
  MEETING_EXAM_TYPE_30P,
  MEETING_EXAM_TYPE_40P, REGISTRATION_ID_TYPE_CONSENT_TO_DUAL_REGISTRATION,
  REGISTRATION_ID_TYPE_REQUEST_FOR_DELETION_SENT,
  REGISTRATION_ID_TYPE_SENT, REGISTRATION_STATUS_BLOCKED,
  REGISTRATION_STATUS_DEREGISTERED, REGISTRATION_STATUS_REGISTERED, REGISTRATION_STATUS_RESIGNATION,
  REGISTRATION_STATUS_SENT_TO_DEREGISTER, REGISTRATION_STATUS_TO_BLOCK,
  REGISTRATION_STATUS_TO_DEREGISTER,
  TEMPLATE_TYPE_AUTHORIZATION,
  TEMPLATE_TYPE_STATEMENT
} from '../../../shared/services/dict.service';
import {HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {DownloaderService} from '../../../shared/services/downloader.service';
import {TemplateService} from '../../../shared/services/template.service';
import {Dictionary} from '../../../shared/interface/dictionary';
import {RegistrationIdentifier} from '../../../shared/interface/registration-identifier';
import {ListResponse} from '../../../shared/interface/response/list-response';
import {OfwcaService} from '../../../shared/services/ofwca.service';
import {
  ConfirmDialogWithReasonComponent,
  ConfirmResult
} from '../../../shared/components/confirm-dialog-with-reason/confirm-dialog-with-reason.component';
import {ExamSelectDialogComponent} from '../../meetings/exam-select-dialog/exam-select-dialog.component';
import {Meeting} from '../../../shared/interface/meeting';
import {MeetingService} from '../../../shared/services/meeting.service';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {Uuid} from '../../../shared/interface/response/uuid';
import {CancelRegistrationDialogComponent} from "../cancel-registration-dialog/cancel-registration-dialog.component";

@Component({
  selector: 'app-registration-details-view',
  templateUrl: './registration-details-view.component.html',
  styleUrls: ['./registration-details-view.component.scss']
})
export class RegistrationDetailsViewComponent implements OnInit, OnDestroy {
  @Input() registration: Observable<Registration>;
  @Output() modifyClicked = new EventEmitter<any>();
  @Output() changed = new EventEmitter<any>();

  readonly IC_LINK4 = IC_LINK4;
  readonly IC_ALLIANZ = IC_ALLIANZ;
  readonly IC_PZU = IC_PZU;
  readonly IC_PZU_LIFE = IC_PZU_LIFE;
  readonly IC_COMPENSA = IC_COMPENSA;
  readonly IC_COMPENSA_LIFE = IC_COMPENSA_LIFE;
  readonly IC_INTER_POLSKA = IC_INTER_POLSKA;
  readonly IC_INTER_POLSKA_LIFE = IC_INTER_POLSKA_LIFE;
  readonly IC_TRASTI = IC_TRASTI;
  readonly DEREGISTER_STATUSES = [
    REGISTRATION_STATUS_DEREGISTERED,
    REGISTRATION_STATUS_TO_DEREGISTER,
    REGISTRATION_STATUS_SENT_TO_DEREGISTER
  ];
  readonly BLOCK_STATUSES = [
    REGISTRATION_STATUS_RESIGNATION,
    REGISTRATION_STATUS_TO_BLOCK,
    REGISTRATION_STATUS_BLOCKED
  ];
  readonly INSURANCE_TYPE_LIFE = INSURANCE_TYPE_LIFE;
  readonly INSURANCE_TYPE_ASSETS = INSURANCE_TYPE_ASSETS;
  readonly CHRONIE_INSURANCE_COMPANIES = [
    IC_WIENER,
    IC_EUROPA
  ];
  readonly IC_WITH_SALES_PROFILES = IC_WITH_SALES_PROFILES;
  registrationObject?: Registration;
  userPermissions: ReplaySubject<UserPermissions>;
  generatingDocument = false;
  registrationIdTypes: {[key: string]: Dictionary} = {};
  registrationIdentifiers: {[key: string]: RegistrationIdentifier} = {};
  canRegisterToExam = false;
  readonly REGISTRATION_ID_TYPE_SENT = REGISTRATION_ID_TYPE_SENT;
  readonly REGISTRATION_ID_TYPE_REQUEST_FOR_DELETION_SENT = REGISTRATION_ID_TYPE_REQUEST_FOR_DELETION_SENT;
  readonly REGISTRATION_ID_TYPE_CONSENT_TO_DUAL_REGISTRATION = REGISTRATION_ID_TYPE_CONSENT_TO_DUAL_REGISTRATION;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private auth: AuthService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private registrationService: RegistrationService,
    private router: Router,
    private dictService: DictService,
    private downloader: DownloaderService,
    private templateService: TemplateService,
    private ofwcaService: OfwcaService,
    private meetingService: MeetingService,
  ) {
    this.userPermissions = auth.getUserPermissions();
  }

  ngOnInit(): void {
    this.registration
      .pipe(takeUntil(this.destroyed$))
      .subscribe((registration: Registration) => {
        this.registrationObject = registration;
        this.loadIdDict();
        this.loadIds();
        this.registrationService.canRegisterToExam(registration.uuid)
          .subscribe((result) => this.canRegisterToExam = result);
      });
  }

  loadIdDict(): void {
    this.dictService.getDictSubject()
      .pipe(first())
      .subscribe((items: Dictionary[]) => {
        items.forEach((dict: Dictionary) => {
          if (
            dict.type === DICT_TYPE_REGISTRATION_ID_TYPE && this.registrationObject
            && this.registrationObject.insuranceCompany.registrationIdTypes.includes(dict.key)
          ) {
            this.registrationIdTypes[dict.key] = dict;
          }
        });
      });
  }

  loadIds(): void {
    this.registrationService.getRegistrationIds(this.registrationObject.uuid)
      .pipe(first())
      .subscribe((response: ListResponse<RegistrationIdentifier>) => {
        response.items.forEach((identifier: RegistrationIdentifier) => {
          this.registrationIdentifiers[identifier.type.key] = identifier;

        });
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  modify(): void {
    this.modifyClicked.emit();
  }


  copyMessage(val: string): void{
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
    this.snack.open('Skopiowano do schowka', '', {duration: 2000});

  }
  delete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć rejestrację?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.registrationService.deleteRegistration(this.registrationObject.uuid)
          .subscribe(() => {
            this.snack.open('Pomyślnie usunięto rejestrację', '', {duration: 2000});
            this.router.navigate(['/rejestracje']);
          });
      }
    });
  }

  generateStatements(): void {
    this.generatingDocument = true;
    this.dictService.getId(TEMPLATE_TYPE_STATEMENT, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generate({
          type: dictId,
          registration: this.registrationObject.uuid,
        }).subscribe((response: Uuid) => {
          const dialogRef = this.dialog.open(JobResultDialogComponent, {
            disableClose: true,
            width: '500px',
            data: {jobId: response.uuid, showProgress: false},
          });

          dialogRef.afterClosed().subscribe(result => {
            this.generatingDocument = false;
          });
        }, (error: HttpErrorResponse) => {
          this.generatingDocument = false;
          if (error.status === 404) {
            this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
            return;
          }

          this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
        });
      });
  }

  generateAuthorization(): void {
    this.generatingDocument = true;
    this.dictService.getId(TEMPLATE_TYPE_AUTHORIZATION, DICT_TYPE_TEMPLATE_TYPE)
      .subscribe((dictId: number) => {
        this.templateService.generateAuthorization({
          type: dictId,
          registration: this.registrationObject.uuid,
        }).subscribe({
          next: (response: HttpResponse<any>) => {
            this.downloader.downloadFile(response);
            this.generatingDocument = false;
          },
          error: (error: HttpErrorResponse) => {
            this.generatingDocument = false;
            if (error.status === 412) {
              this.snack.open('Dla wybranej rejestracji upoważnienie jest już wygenerowanie', '', {duration: 2000});
              return;
            }
            if (error.status === 406) {
              this.snack.open('Brak możliwości wygenerowania upoważnienia', '', {duration: 2000});
              return;
            }
            if (error.status === 404) {
              this.snack.open('Brak szablonów dla tego typu dokumentu', '', {duration: 2000});
              return;
            }

            this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
          }
        });
      });
  }

  deregisterRegistration(): void {
    // const dialogRef = this.dialog.open(ConfirmDialogWithReasonComponent, {
    //   width: '500px',
    //   data: {text: 'Czy na pewno chcesz wyrejestrować OFWCA w tym TU?'},
    // });
    //
    // dialogRef.afterClosed().subscribe((result: ConfirmResult) => {
    //   if (result.accepted) {
    //     // this.registrationService.cancelRegistration(this.registrationObject.uuid, result.reason)
    //     //   .subscribe(() => {
    //     //     this.snack.open('Pomyślnie zaktualizowano rejestrację', '', {duration: 2000});
    //     //     this.changed.emit();
    //     //   });
    //   }
    // });
    const dialogRef = this.dialog.open(CancelRegistrationDialogComponent, {
      width: '80vw',
      maxHeight: '90vh',
      data: {registrationObject: this.registrationObject,text: 'Czy na pewno chcesz wyrejestrować OFWCA w tym TU?'},
    });

    dialogRef.afterClosed().subscribe(() => {
      this.changed.emit();
    });
  }

  deregisterAllRegistration(): void {
    // const dialogRef = this.dialog.open(ConfirmDialogWithReasonComponent, {
    //   width: '500px',
    //   data: {text: 'Czy na pewno chcesz wyrejestrować OFWCA we wszystkich TU?'},
    // });
    //
    // dialogRef.afterClosed().subscribe((result: ConfirmResult) => {
    //   if (result.accepted) {
    //     this.ofwcaService.cancelRegistrations(this.registrationObject.ofwca.uuid, result.reason)
    //       .subscribe(() => {
    //         this.snack.open('Pomyślnie zaktualizowano rejestracje', '', {duration: 2000});
    //         this.changed.emit();
    //       });
    //   }
    // });

    const dialogRef = this.dialog.open(CancelRegistrationDialogComponent, {
      width: '80vw',
      maxHeight: '90vh',
      data: {ofwcaObject: this.registrationObject.ofwca, text: 'Czy na pewno chcesz wyrejestrować OFWCA we wszystkich TU?'},
    });

    dialogRef.afterClosed().subscribe(() => {
      this.changed.emit();
    });
  }

  blockRegistration(): void {
    const dialogRef = this.dialog.open(ConfirmDialogWithReasonComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz zgłosić rejestrację OFWCA do zablokowania w tym TU?'},
    });

    dialogRef.afterClosed().subscribe((result: ConfirmResult) => {
      if (result.accepted) {
        this.registrationService.blockRegistration(this.registrationObject.uuid, result.reason)
          .subscribe(() => {
            this.snack.open('Pomyślnie zaktualizowano rejestrację', '', {duration: 2000});
            this.changed.emit();
          });
      }
    });
  }

  blockAllRegistration(): void {
    const dialogRef = this.dialog.open(ConfirmDialogWithReasonComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz zgłosić do zablokowania wszystkie rejestracje OFWCA?'},
    });

    dialogRef.afterClosed().subscribe((result: ConfirmResult) => {
      if (result.accepted) {
        this.ofwcaService.blockRegistrations(this.registrationObject.ofwca.uuid, result.reason)
          .subscribe(() => {
            this.snack.open('Pomyślnie zaktualizowano rejestracje', '', {duration: 2000});
            this.changed.emit();
          });
      }
    });
  }

  registerToExam(): void {
    const dialogRef = this.dialog.open(ExamSelectDialogComponent, {
      width: '80vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        insuranceCompanyId: (this.registrationObject.ofwca.hasPassedExam100pAssets && this.registrationObject.ofwca.hasPassedExam100pLife) ?
          this.registrationObject.insuranceCompany.id : null,
        missingExamKeys: (this.registrationObject.ofwca.hasPassedExam100pAssets && this.registrationObject.ofwca.hasPassedExam100pLife) ?
          [MEETING_EXAM_TYPE_30P, MEETING_EXAM_TYPE_40P] : [MEETING_EXAM_TYPE_100P]
      }
    });

    dialogRef.afterClosed().subscribe((meeting: Meeting) => {
      if (meeting) {
        this.meetingService.registerToMeeting(meeting.uuid, [this.registrationObject.ofwca.uuid])
          .subscribe(() => {
            this.snack.open('OFWCA została zapisana na egzamin', '', {duration: 2000});
            this.changed.emit();
          }, (error: HttpErrorResponse) => {
            this.snack.open('Przy zapisywaniu na egzamin wystąpił błąd', error.status.toString(), {duration: 2000});
          });
      }
    });
  }
}
